import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Check, X, Clock, CalendarDays, Sliders, Info } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { useVersion, formatVersion } from "@/context/VersionContext";

export default function Settings() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "coach" });
  const [aiStatus, setAiStatus] = useState(null);
  const [thresholds, setThresholds] = useState(null);
  const [thresholdsDefault, setThresholdsDefault] = useState(null);
  const [thrSaving, setThrSaving] = useState(false);
  const versionInfo = useVersion();

  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => {
    load();
    api.get("/ai/status").then((r) => setAiStatus(r.data)).catch(() => setAiStatus(null));
    api.get("/settings/matching-thresholds")
      .then((r) => { setThresholds(r.data.thresholds); setThresholdsDefault(r.data.defaults); })
      .catch(() => null);
  }, []);

  const saveThresholds = async () => {
    setThrSaving(true);
    try {
      await api.put("/settings/matching-thresholds", thresholds);
      toast.success("Soglie salvate");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore salvataggio soglie");
    } finally { setThrSaving(false); }
  };
  const resetThresholds = () => { if (thresholdsDefault) setThresholds({ ...thresholdsDefault }); };

  const { pending, active } = useMemo(() => {
    const p = [], a = [];
    users.forEach((u) => { (u.status === "pending" ? p : a).push(u); });
    return { pending: p, active: a };
  }, [users]);

  const save = async () => {
    try { await api.post("/users", form); toast.success("Utente creato"); setOpen(false); load();
      setForm({ email: "", password: "", name: "", role: "coach" });
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const del = async (id) => { if (!window.confirm("Eliminare l'utente?")) return; await api.delete(`/users/${id}`); load(); };
  const approve = async (id) => { try { await api.post(`/users/${id}/approve`); toast.success("Utente approvato"); load(); } catch { toast.error("Errore"); } };
  const reject = async (id) => { if (!window.confirm("Rifiutare la richiesta?")) return; try { await api.post(`/users/${id}/reject`); toast.success("Richiesta rifiutata"); load(); } catch { toast.error("Errore"); } };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-4xl font-bold tracking-tight">Impostazioni</h1>
          <p className="text-zinc-500 mt-1">Utenti e configurazione.</p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/impostazioni/stagioni">
            <Button data-testid="seasons-settings-btn" variant="outline" className="h-12">
              <CalendarDays className="w-4 h-4 mr-2" />Stagioni e categorie
            </Button>
          </Link>
          <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-user-btn" className="h-12 bg-black hover:bg-zinc-800"><Plus className="w-4 h-4 mr-2" />Nuovo utente</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Nuovo utente</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <div><Label>Nome</Label><Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} /></div>
              <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} /></div>
              <div><Label>Password</Label><Input type="password" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} /></div>
              <div>
                <Label>Ruolo</Label>
                <Select value={form.role} onValueChange={(v) => setForm({...form, role: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Amministratore</SelectItem>
                    <SelectItem value="coach">Maestro / Allenatore</SelectItem>
                    <SelectItem value="trainer">Tecnico / Preparatore</SelectItem>
                    <SelectItem value="athlete">Atleta</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter><Button data-testid="user-save" onClick={save} className="bg-black hover:bg-zinc-800">Salva</Button></DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {pending.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-md overflow-hidden" data-testid="pending-users-section">
          <div className="px-5 py-3 border-b border-amber-200 flex items-center gap-2">
            <Clock className="w-4 h-4 text-amber-700" />
            <div className="font-heading font-semibold text-amber-900">Richieste in attesa ({pending.length})</div>
          </div>
          <table className="w-full">
            <thead className="text-xs uppercase tracking-wider text-amber-800 border-b border-amber-200">
              <tr><th className="text-left px-5 py-2">Nome</th><th className="text-left px-5 py-2">Email</th><th className="text-left px-5 py-2">Ruolo</th><th className="text-left px-5 py-2">Nota</th><th className="w-40"></th></tr>
            </thead>
            <tbody>
              {pending.map((u) => (
                <tr key={u.id} className="border-b border-amber-100 last:border-0" data-testid={`pending-row-${u.id}`}>
                  <td className="px-5 py-3">{u.name}</td>
                  <td className="px-5 py-3 font-mono text-sm">{u.email}</td>
                  <td className="px-5 py-3"><span className="text-[11px] uppercase tracking-wider px-2 py-1 bg-white rounded">{u.role}</span></td>
                  <td className="px-5 py-3 text-xs text-zinc-600">{u.note || "—"}</td>
                  <td className="px-5 py-3 text-right">
                    <div className="inline-flex gap-2">
                      <Button data-testid={`approve-${u.id}`} size="sm" onClick={() => approve(u.id)} className="bg-emerald-600 hover:bg-emerald-700"><Check className="w-3.5 h-3.5 mr-1" />Approva</Button>
                      <Button data-testid={`reject-${u.id}`} size="sm" variant="outline" onClick={() => reject(u.id)} className="text-red-600 border-red-200 hover:bg-red-50"><X className="w-3.5 h-3.5 mr-1" />Rifiuta</Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="bg-white border border-zinc-200 rounded-md overflow-hidden">
        <div className="px-5 py-3 border-b border-zinc-200 font-heading font-semibold">Utenti attivi ({active.length})</div>
        <table className="w-full">
          <thead className="text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-200">
            <tr><th className="text-left px-5 py-3">Nome</th><th className="text-left px-5 py-3">Email</th><th className="text-left px-5 py-3">Ruolo</th><th className="text-left px-5 py-3">Stato</th><th></th></tr>
          </thead>
          <tbody>
            {active.map((u) => (
              <tr key={u.id} className="border-b border-zinc-100">
                <td className="px-5 py-4">{u.name}</td>
                <td className="px-5 py-4 font-mono text-sm">{u.email}</td>
                <td className="px-5 py-4"><span className="text-[11px] uppercase tracking-wider px-2 py-1 bg-zinc-100 rounded">{u.role}</span></td>
                <td className="px-5 py-4">
                  {u.status === "rejected" ? (
                    <span className="text-[11px] uppercase tracking-wider px-2 py-1 bg-red-100 text-red-700 rounded">Rifiutato</span>
                  ) : (
                    <span className="text-[11px] uppercase tracking-wider px-2 py-1 bg-emerald-100 text-emerald-700 rounded">Attivo</span>
                  )}
                </td>
                <td className="px-5 py-4 text-right">
                  {u.status === "rejected" && (
                    <Button data-testid={`reactivate-${u.id}`} size="sm" variant="outline" onClick={() => approve(u.id)} className="mr-2">Riattiva</Button>
                  )}
                  <button onClick={() => del(u.id)} className="text-zinc-400 hover:text-red-600 p-2"><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {thresholds && (
        <div className="bg-white border border-zinc-200 rounded-md overflow-hidden" data-testid="matching-thresholds-section">
          <div className="px-5 py-3 border-b border-zinc-200 flex items-center gap-2">
            <Sliders className="w-4 h-4 text-zinc-600" />
            <div className="font-heading font-semibold">Importazione · Soglie di matching</div>
          </div>
          <div className="p-5 space-y-4">
            <p className="text-sm text-zinc-500">
              Configurazione delle soglie di confidenza usate dal Matching Engine per riconoscere gli atleti duplicati durante le importazioni.
              Valori da 0 a 100. Devono essere in ordine decrescente.
            </p>
            <div className="grid md:grid-cols-4 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-500">Match certo</Label>
                <Input data-testid="threshold-certain" type="number" min={0} max={100}
                  value={thresholds.certain}
                  onChange={(e) => setThresholds({ ...thresholds, certain: Number(e.target.value) })}
                  className="mt-1" />
                <div className="text-[10px] text-zinc-400 mt-1">Default: {thresholdsDefault?.certain ?? 95}</div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-500">Match molto probabile</Label>
                <Input data-testid="threshold-very-probable" type="number" min={0} max={100}
                  value={thresholds.very_probable}
                  onChange={(e) => setThresholds({ ...thresholds, very_probable: Number(e.target.value) })}
                  className="mt-1" />
                <div className="text-[10px] text-zinc-400 mt-1">Default: {thresholdsDefault?.very_probable ?? 85}</div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-500">Match probabile</Label>
                <Input data-testid="threshold-probable" type="number" min={0} max={100}
                  value={thresholds.probable}
                  onChange={(e) => setThresholds({ ...thresholds, probable: Number(e.target.value) })}
                  className="mt-1" />
                <div className="text-[10px] text-zinc-400 mt-1">Default: {thresholdsDefault?.probable ?? 70}</div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-500">Possibile duplicato</Label>
                <Input data-testid="threshold-possible" type="number" min={0} max={100}
                  value={thresholds.possible}
                  onChange={(e) => setThresholds({ ...thresholds, possible: Number(e.target.value) })}
                  className="mt-1" />
                <div className="text-[10px] text-zinc-400 mt-1">Default: {thresholdsDefault?.possible ?? 60}</div>
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button data-testid="save-thresholds-btn" onClick={saveThresholds} disabled={thrSaving} className="bg-black hover:bg-zinc-800">
                {thrSaving ? "Salvataggio..." : "Salva soglie"}
              </Button>
              <Button data-testid="reset-thresholds-btn" variant="outline" onClick={resetThresholds} disabled={thrSaving}>
                Ripristina predefiniti
              </Button>
            </div>
          </div>
        </div>
      )}

      {aiStatus && (
        <div className="bg-white border border-zinc-200 rounded-md overflow-hidden" data-testid="admin-ai-status">
          <div className="px-5 py-3 border-b border-zinc-200 font-heading font-semibold">Assistente AI · configurazione tecnica</div>
          <div className="p-5 grid md:grid-cols-3 gap-3 text-sm">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-500">Stato</div>
              <div className="font-medium">{aiStatus.enabled ? "Attivo" : "Non configurato"}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-500">Modello</div>
              <div className="font-mono">{aiStatus.model || "—"}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-500">Chiave</div>
              <div className="font-mono">{aiStatus.key_source === "openai_key" ? "OpenAI API key personale" : (aiStatus.key_source ? "Chiave universale Emergent" : "—")}</div>
            </div>
          </div>
          <div className="px-5 pb-5 text-xs text-zinc-500">Queste informazioni sono visibili solo agli amministratori.</div>
        </div>
      )}

      <div className="bg-white border border-zinc-200 rounded-md overflow-hidden" data-testid="settings-version-section">
        <div className="px-5 py-3 border-b border-zinc-200 flex items-center gap-2">
          <Info className="w-4 h-4 text-zinc-600" />
          <div className="font-heading font-semibold">Versione applicazione</div>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid md:grid-cols-3 gap-4 text-sm">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-500">Versione</div>
              <div className="font-mono text-2xl" data-testid="settings-version-number">{formatVersion(versionInfo)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-500">Build</div>
              <div className="font-mono" data-testid="settings-version-build">{versionInfo.build_date || "—"}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-500">Codename</div>
              <div className="font-medium">{versionInfo.codename || "—"}</div>
            </div>
          </div>
          {(versionInfo.changelog || []).length > 0 && (
            <div className="pt-3 border-t border-zinc-100" data-testid="settings-changelog">
              <div className="text-xs uppercase tracking-wider text-zinc-500 mb-3">Changelog</div>
              <div className="space-y-4 max-h-80 overflow-y-auto pr-2">
                {(versionInfo.changelog || []).map((e) => (
                  <div key={e.version} data-testid={`changelog-entry-${e.version}`}>
                    <div className="flex items-baseline gap-2 mb-1">
                      <span className="font-mono font-semibold text-sm">v{e.version}</span>
                      <span className="text-[11px] text-zinc-400 font-mono">{e.date}</span>
                      {e.codename && <span className="text-[11px] text-zinc-500 italic">"{e.codename}"</span>}
                    </div>
                    <ul className="text-sm text-zinc-600 list-disc list-inside space-y-0.5 ml-1">
                      {e.items.map((item, i) => <li key={i}>{item}</li>)}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
